// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/AutocompleteCommand.module.css';
import type {AutocompleteListProps} from '@app/features/channel/components/AutocompleteTypes';
import {isCommand} from '@app/features/channel/components/AutocompleteTypes';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {observer} from 'mobx-react-lite';

export const AutocompleteCommand = observer(
	({
		onSelect,
		keyboardFocusIndex,
		hoverIndex,
		options,
		onMouseEnter,
		onMouseLeave,
		rowRefs,
		getOptionId,
	}: AutocompleteListProps) => {
		const commands = options.filter(isCommand);

		return commands.map((option, index) => {
			const cmd = option.command;
			const isActive = index === keyboardFocusIndex || index === hoverIndex;

			let avatarUrl: string | null = null;
			let botName: string | null = null;

			if (cmd.type === 'bot') {
				const botId = cmd.botId ?? cmd.applicationId;
				avatarUrl = AvatarUtils.getUserAvatarURL(
					{id: botId, avatar: cmd.botAvatar ?? null},
					false,
					28,
				);
				botName = cmd.botName ?? 'Bot';
			}

			const key = cmd.type === 'bot'
				? `${cmd.applicationId}-${cmd.name}-${index}`
				: `${cmd.name}-${index}`;

			return (
				<button
					type="button"
					key={key}
					id={getOptionId?.(index)}
					className={styles.button}
					onClick={() => onSelect(option)}
					onPointerEnter={() => onMouseEnter(index)}
					onPointerLeave={onMouseLeave}
					ref={
						rowRefs
							? (node: HTMLButtonElement | null) => {
									rowRefs.current[index] = node;
								}
							: undefined
					}
					role="option"
					aria-selected={index === keyboardFocusIndex}
					tabIndex={-1}
					data-flx="channel.autocomplete-command.autocomplete-item.select"
				>
					<div
						className={`${styles.container} ${isActive ? styles.selected : ''}`}
						data-flx="channel.autocomplete-command.container"
					>
						{avatarUrl ? (
							<img
								src={avatarUrl}
								alt={botName ?? 'Bot'}
								className={styles.avatar}
								onError={(e) => {
									// Fallback if image fails to load
									e.currentTarget.style.display = 'none';
								}}
								data-flx="channel.autocomplete-command.avatar"
							/>
						) : (
							<div className={styles.defaultIcon} data-flx="channel.autocomplete-command.default-icon">/</div>
						)}

						<div className={styles.content} data-flx="channel.autocomplete-command.content">
							<div className={styles.nameRow} data-flx="channel.autocomplete-command.name-row">
								<span className={styles.name} data-flx="channel.autocomplete-command.name">{cmd.name}</span>
							</div>
							{cmd.description ? (
								<div className={styles.description} data-flx="channel.autocomplete-command.description">
									{cmd.description}
								</div>
							) : null}
						</div>

						{botName ? (
							<div className={styles.botName} data-flx="channel.autocomplete-command.bot-name">
								{botName}
							</div>
						) : null}
					</div>
				</button>
			);
		});
	},
);
